import { collection, onSnapshot, query } from "firebase/firestore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { setErrorStatus } from "../Redux/status/action";
import {
  getAllWorkforceSummaryData,
  setWorkforceData,
  setWorkforceResults
} from "../Redux/workforce/action";
import { database } from "../Services/firebase";
import { isValidArray, isValidObject } from "../Services/validators";
import {
  getValidReportName,
  workforceTableField
} from "../Utils/Constants/Workforce";

import { search } from "fast-fuzzy";
import { setTablePageNumber } from "../Redux/table/action";
import { exportFiles } from "../Services/api";
import { getAveragePercentageOfWorkforceSummary } from "../Services/database";
import {
  attendanceQueryBuilder,
  capturesQueryBuilder,
  workforceQueryBuilder
} from "../Utils/Constants/Calls";
import {
  capitalizeFirstLetter,
  dateAndTimeConverter,
  isFilterApplied
} from "../Utils/Constants/Constants";
import { getLocationIdWithPctNo } from "../Utils/Constants/Locations";

export const listenerDataLimit = 50;

export default function useWorkForce(props) {
  const workforceListener = useRef(null);
  const workforceData = useRef({});
  const workforceResults = useRef({});
  const pageNumber = useRef(1);
  const isInitialLoad = useRef(true);
  const summaryDataCache = useRef({});
  const sortChanged = useRef(false);
  const processingQueue = useRef(new Set());
  const previousDateRange = useRef(props.filterData.dateRange);

  const [workforceLoading, setWorkforceLoading] = useState({
    assets: false,
    pagination: false,
    search: false,
    export: false
  });

  useEffect(() => {
    sortChanged.current = true;
  }, [props.sort]);

  useEffect(() => {
    getAllWorkforceSummaryData();
    // eslint-disable-next-line
  }, [props.filterData]);

  useEffect(() => {
    const currentDateRange = props.filterData.dateRange;
    const prevDateRange = previousDateRange.current;

    if (JSON.stringify(currentDateRange) !== JSON.stringify(prevDateRange)) {

      summaryDataCache.current = {};
      processingQueue.current.clear();

      previousDateRange.current = currentDateRange;

      const employeeIds = Object.keys(workforceData.current);
      if (employeeIds.length > 0) {
        refreshAttendanceForEmployees(employeeIds);
      }
    }
    // eslint-disable-next-line
  }, [props.filterData.dateRange]);

  const fetchSummaryDataForEmployees = useCallback(
    async (employeeIds, dateRange, validRoleIds, forceRefresh = false) => {
      if (!isValidArray(employeeIds)) return {};

      const uncachedIds = forceRefresh
        ? employeeIds
        : employeeIds.filter(
            (id) =>
              !processingQueue.current.has(id) && !summaryDataCache.current[id]
          );

      if (uncachedIds.length === 0) return {};

      if (forceRefresh) {
        uncachedIds.forEach((id) => {
          delete summaryDataCache.current[id];
          processingQueue.current.delete(id);
        });
      }

      uncachedIds.forEach((id) => processingQueue.current.add(id));

      try {

        const results = await Promise.all(
          uncachedIds.map((id) =>
            getAveragePercentageOfWorkforceSummary(id, dateRange)
          )
        );

        uncachedIds.forEach((id, index) => {
          const resultData = results[index];

          const attendance =
            isValidArray(validRoleIds) &&
            isValidArray(workforceData.current[id]?.roles) &&
            workforceData.current[id]?.roles?.some((roleId) =>
              validRoleIds.includes(roleId)
            )
              ? Object.values(resultData)[0]
              : "N/A";

          summaryDataCache.current[id] = attendance;

        });

        return {};
      } catch (error) {
        console.error("Error fetching summary data:", error);
        return {};
      } finally {
        uncachedIds.forEach((id) => processingQueue.current.delete(id));
      }
    },
    []
  );

  const updateWorkforceWithSummary = useCallback(
    async (
      workforce,
      employeeIds,
      dateRange,
      validRoleIds,
      forceRefresh = false
    ) => {
      await fetchSummaryDataForEmployees(
        employeeIds,
        dateRange,
        validRoleIds,
        forceRefresh
      );

      const updatedWorkforce = { ...workforce };

      Object.keys(updatedWorkforce).forEach((id) => {
        if (summaryDataCache.current[id] !== undefined) {
          updatedWorkforce[id] = {
            ...updatedWorkforce[id],
            attendance: summaryDataCache.current[id]
          };
        }
      });

      return updatedWorkforce;
    },
    [fetchSummaryDataForEmployees]
  );

  const refreshAttendanceForEmployees = useCallback(
    async (employeeIds) => {
      if (!isValidArray(employeeIds)) return;

      const updatedWorkforce = await updateWorkforceWithSummary(
        workforceData.current,
        employeeIds,
        props.filterData.dateRange,
        props.validRoles,
        true
      );

      workforceData.current = updatedWorkforce;
      setWorkforceData(updatedWorkforce);
    },
    [props.filterData.dateRange, props.validRoles, updateWorkforceWithSummary]
  );

  const subscribeToWorkforce = useCallback(() => {
    const collectionRef = query(collection(database, "employees"));

    return onSnapshot(
      collectionRef,
      async (dataSnapshot) => {
        if (isInitialLoad.current) {
          let initialWorkforce = {};
          const employeeIds = [];

          dataSnapshot.forEach((doc) => {
            const id = doc.id;
            initialWorkforce[id] = {
              ...doc.data(),
              documentId: id,
              attendance: null
            };
            // archived employees are kept for the status filter but don't
            // need attendance summaries
            if (!doc.data().actions?.deletedAt) {
              employeeIds.push(id);
            }
          });


          const updatedWorkforce = await updateWorkforceWithSummary(
            initialWorkforce,
            employeeIds,
            props.filterData.dateRange,
            props.validRoles
          );

          workforceData.current = updatedWorkforce;
          setWorkforceData(updatedWorkforce);
          isInitialLoad.current = false;
          return;
        }

        const deletedIds = [];
        const modifiedIds = [];
        const summaryUpdateIds = [];
        let updatedWorkforce = { ...workforceData.current };

        dataSnapshot.docChanges().forEach((change) => {
          const docId = change.doc.id;
          const docData = change.doc.data();

          if (change.type === "modified") {

            if (!docData.actions?.deletedAt) {
              const existingEmployee = workforceData.current[docId];
              const hasLastCapturedAtChanged =
                existingEmployee?.captureStatus?.lastCapturedAt !==
                docData.captureStatus?.lastCapturedAt;

              updatedWorkforce[docId] = {
                ...docData,
                documentId: docId,
                attendance: summaryDataCache.current[docId] || null
              };
              modifiedIds.push(docId);

              // just unarchived: no attendance was fetched while archived
              const wasArchived = !!existingEmployee?.actions?.deletedAt;

              if (hasLastCapturedAtChanged || wasArchived) {
                delete summaryDataCache.current[docId];
                summaryUpdateIds.push(docId);
              }
            } else {
              updatedWorkforce[docId] = {
                ...docData,
                documentId: docId,
                attendance: null
              };
              delete summaryDataCache.current[docId];
              modifiedIds.push(docId);
            }
          } else if (change.type === "added" && !isInitialLoad.current) {
            updatedWorkforce[docId] = {
              ...docData,
              documentId: docId,
              attendance: null
            };
            modifiedIds.push(docId);

            if (
              !docData.actions?.deletedAt &&
              docData.captureStatus?.lastCapturedAt
            ) {
              summaryUpdateIds.push(docId);
            }
          } else if (change.type === "removed") {
            deletedIds.push(docId);
          }
        });

        if (deletedIds.length > 0) {
          deletedIds.forEach((id) => {
            delete updatedWorkforce[id];
            delete summaryDataCache.current[id];
          });
        }

        if (summaryUpdateIds.length > 0) {

          const finalWorkforce = await updateWorkforceWithSummary(
            updatedWorkforce,
            summaryUpdateIds,
            props.filterData.dateRange,
            props.validRoles
          );

          workforceData.current = finalWorkforce;
          setWorkforceData(finalWorkforce);
        } else if (modifiedIds.length > 0 || deletedIds.length > 0) {
          workforceData.current = updatedWorkforce;
          setWorkforceData(updatedWorkforce);
        }
      },
      (error) => {
        console.error(error, "from employee listener");
        setErrorStatus(error);
      }
    );
  }, [
    props.filterData.dateRange,
    props.validRoles,
    updateWorkforceWithSummary
  ]);

  const processedData = useMemo(() => {
    const processData = (workforceData) => {
      if (
        !workforceData ||
        !isValidObject(workforceData) ||
        !isValidObject(props.locationData)
      ) {
        return [];
      }

      let data = Object.values(workforceData).filter(
        (item) => !item?.actions?.deletedAt
      );

      if (props.filterData && props.locationData) {
        data = filterWorkforce(
          workforceData,
          Object.values(props.locationData),
          props.filterData,
          props.roles
        );
      }

      if (isValidArray(data) && props.searchKey) {
        data = search(props.searchKey, data, {
          keySelector: (item) =>
            `${item.name} ${item.employeeId} ${item.phoneNumber} ${item.email}`,
          threshold: 0.8
        });
      }

      if (isValidArray(data) && props.sort && !props.searchKey) {
        data = sortedWorkforce(data, props.sort, props.roles);
      }

      return data;
    };

    return processData(workforceData.current);
    // eslint-disable-next-line
  }, [
    workforceData.current,
    props.filterData,
    props.searchKey,
    props.sort,
    props.locationData,
    props.roles
  ]);

  useEffect(() => {
    if (sortChanged.current) {
      pageNumber.current = 1;
      sortChanged.current = false;
      setTablePageNumber("WORKFORCE", pageNumber.current);
    }

    const modifiedData = isValidArray(processedData)
      ? processedData.reduce((acc, item) => {
          acc[item.documentId] = item;
          return acc;
        }, {})
      : {};

    workforceResults.current = modifiedData;
    const startIndex = (pageNumber.current - 1) * 50;
    const endIndex = startIndex + 50;

    setTablePageNumber("WORKFORCE", pageNumber.current);
    setWorkforceResults(
      Object.values(modifiedData).slice(startIndex, endIndex),
      Object.values(modifiedData).length
    );
  }, [processedData]);

  const getWorkforceNextPage = useCallback(() => {
    const data = Object.values(workforceResults.current);
    pageNumber.current += 1;
    setTablePageNumber("WORKFORCE", pageNumber.current);
    setWorkforceResults(
      handlePagination(data, pageNumber.current),
      data.length
    );
  }, []);

  const getWorkforcePreviousPage = useCallback(() => {
    const data = Object.values(workforceResults.current);
    pageNumber.current -= 1;
    setTablePageNumber("WORKFORCE", pageNumber.current);
    setWorkforceResults(
      handlePagination(data, pageNumber.current),
      data.length
    );
  }, []);

  const getWorkforceSummary = useCallback(
    async (data) => {
      const getValidFilter = (name) => {
        if (name === "workforce") {
          return workforceQueryBuilder(props.filterData, props.roles);
        }
        if (name === "captures") {
          return capturesQueryBuilder(props.filterData, props.roles);
        }
        if (name === "attendance") {
          return attendanceQueryBuilder(props.filterData, props.roles);
        }
      };

      await Promise.all(
        data.map(async (ele) => {
          await downloadSummary({
            accessToken: props.accessToken,
            workforceLoading: workforceLoading,
            filterQuery: getValidFilter(getValidReportName(ele)),
            setWorkforceLoading: setWorkforceLoading,
            type: getValidReportName(ele)
          });
        })
      );
    },
    [props.accessToken, props.filterData, props.roles, workforceLoading]
  );

  useEffect(() => {
    if (props.isAuth && workforceListener.current === null) {
      workforceListener.current = subscribeToWorkforce();
    } else if (
      props.isAuth === false &&
      typeof workforceListener.current === "function"
    ) {
      workforceListener.current();
      workforceListener.current = null;
      summaryDataCache.current = {};
      processingQueue.current.clear();
    }
  }, [props.isAuth, subscribeToWorkforce]);

  return {
    getWorkforceNextPage,
    getWorkforcePreviousPage,
    getWorkforceSummary,
    workforceLoading,
    refreshAttendanceForEmployees
  };
}

const filterWorkforce = (workforce, locationData, filter, roles) => {
  let updatedData = [];
  // status filter: archived only when explicitly selected, active otherwise
  const showArchived = filter?.status?.includes("Archived");
  const workforceData = Object.values(workforce).filter(
    (item) => !!item?.actions?.deletedAt === !!showArchived
  );

  if (isFilterApplied(filter, "WORKFORCE").validation) {
    const _data = workforceData?.filter((item) => {
      // role filter
      if (
        filter?.role?.length > 0 &&
        !filter?.role.some((role) =>
          getRolesNameFromIds(item.roles, roles)?.includes(role)
        )
      ) {
        return false;
      }

      // assignedLocations filter
      if (
        filter?.assignedLocations?.length > 0 &&
        !item?.connected?.location?.some((location) =>
          getLocationIdWithPctNo(
            locationData,
            filter?.assignedLocations?.map((data) => data.split(" - ")[0])
          ).includes(location)
        )
      ) {
        return false;
      }

      // pending captureApproval filter
      if (
        filter?.captureApproval?.length > 0 &&
        !item?.captureApproval === true
      ) {
        return false;
      }

      return true;
    });
    updatedData = _data;
  } else {
    updatedData = workforceData;
  }

  return updatedData;
};

const downloadSummary = async (props) => {
  try {
    props.setWorkforceLoading({ ...props.workforceLoading, export: true });
    const blob = await exportFiles(
      props.accessToken,
      props.filterQuery,
      props.type
    );

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    link.setAttribute(
      "download",
      `${capitalizeFirstLetter(props.type)} Report @${dateAndTimeConverter(
        Date.now(),
        "Time"
      ).replace(":", "-")}, ${dateAndTimeConverter(
        Date.now(),
        "cardDate"
      ).replace(":", "-")}.xlsx`
    );
    document.body.appendChild(link);
    link.click();

    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    props.setWorkforceLoading({ ...props.workforceLoading, export: false });
  } catch (error) {
    props.setWorkforceLoading({ ...props.workforceLoading, export: false });
    console.error(
      `There was an error downloading the ${capitalizeFirstLetter(
        props.type
      )} file:`,
      error
    );
  }
};

const sortedWorkforce = (data, sort, roles) => {
  return data?.sort((a, b) => {
    let fieldA, fieldB;

    switch (sort?.title) {
      case "LAST CAPTURED AT":
        fieldA = a?.captureStatus?.lastCapturedAt;
        fieldB = b?.captureStatus?.lastCapturedAt;
        break;

      case "LAST CAPTURED IN":
        fieldA = a?.captureStatus?.location?.PCTNo;
        fieldB = b?.captureStatus?.location?.PCTNo;
        break;

      default:
        fieldA = a?.[workforceTableField(sort.title, "sort")];
        fieldB = b?.[workforceTableField(sort.title, "sort")];
        break;
    }

    if (["PCT #", "LAST CAPTURED IN"].includes(sort.title)) {
      return sort.ascending
        ? fieldA
            ?.toString()
            ?.localeCompare(fieldB, undefined, { numeric: true })
        : fieldB
            ?.toString()
            ?.localeCompare(fieldA, undefined, { numeric: true });
    }

    if (["LAST CAPTURED AT", "ATTENDANCE", "CONTACT"].includes(sort.title)) {
      if (typeof fieldA === "boolean" && typeof fieldB === "boolean") {
        return sort.ascending
          ? Number(fieldA) - Number(fieldB)
          : Number(fieldB) - Number(fieldA);
      } else if (!isNaN(fieldA) && !isNaN(fieldB)) {
        return sort.ascending ? fieldA - fieldB : fieldB - fieldA;
      } else if (fieldA === "N/A" || fieldB === "N/A") {
        if (sort.ascending) {
          return fieldA === "N/A" ? -1 : 1;
        } else {
          return fieldA === "N/A" ? 1 : -1;
        }
      }
    } else if (sort.title === "ROLE") {
      const rolesA = getRolesNameFromIds(fieldA, roles);
      const rolesB = getRolesNameFromIds(fieldB, roles);
      const stringA = rolesA?.join(", ").toLowerCase();
      const stringB = rolesB?.join(", ").toLowerCase();
      if (stringA < stringB) {
        return sort.ascending ? -1 : 1;
      }
      if (stringA > stringB) {
        return sort.ascending ? 1 : -1;
      }
    } else {
      const stringA = String(fieldA || "")
        .trim()
        .toLowerCase();
      const stringB = String(fieldB || "")
        .trim()
        .toLowerCase();
      if (stringA < stringB) {
        return sort.ascending ? -1 : 1;
      }
      if (stringA > stringB) {
        return sort.ascending ? 1 : -1;
      }
    }

    return 0;
  });
};

const handlePagination = (data, currentPage) => {
  const start = (currentPage - 1) * 50;
  const end = currentPage * 50;
  return data.slice(start, end);
};

const getRolesNameFromIds = (roleIds, roles) => {
  return roleIds?.map((roleId) => {
    const role = roles?.[roleId]?.name;
    return role ? capitalizeFirstLetter(role) : "N/A";
  });
};
